import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';

const OUTPUT_SIZE = 512; // final square output px

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

// Cropper ma dekhiyeko exact area nai circular PNG ma convert garne
async function makeCircularFile(imageSrc, pixelCrop, rotation) {
  const image = await loadImage(imageSrc);

  const out = document.createElement('canvas');
  out.width = OUTPUT_SIZE;
  out.height = OUTPUT_SIZE;
  const octx = out.getContext('2d');

  // Crop rect ko center (original image coords ma) — yehi point output ko center ma aunu parchha
  const pcx = pixelCrop.x + pixelCrop.width / 2;
  const pcy = pixelCrop.y + pixelCrop.height / 2;
  // Crop width -> output size scale (zoom already pixelCrop size ma included)
  const s = OUTPUT_SIZE / Math.max(1, pixelCrop.width);
  const cx = OUTPUT_SIZE / 2;

  octx.translate(cx, cx);
  octx.rotate((rotation * Math.PI) / 180);
  octx.scale(s, s);
  octx.translate(-pcx, -pcy);
  octx.drawImage(image, 0, 0);

  // Circular mask — corners transparent
  octx.setTransform(1, 0, 0, 1, 0, 0);
  octx.globalCompositeOperation = 'destination-in';
  octx.fillStyle = '#ffffff';
  octx.beginPath();
  octx.arc(cx, cx, cx, 0, Math.PI * 2);
  octx.closePath();
  octx.fill();
  octx.globalCompositeOperation = 'source-over';

  const blob = await new Promise((resolve) =>
    out.toBlob((b) => resolve(b), 'image/png')
  );
  if (!blob) throw new Error('Crop failed');
  return new File([blob], `team-photo-${Date.now()}.png`, { type: 'image/png' });
}

function CircleCropModal({ imageSrc, onCancel, onApply }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const applyCrop = async () => {
    if (!croppedAreaPixels) return;
    setApplying(true);
    try {
      const file = await makeCircularFile(imageSrc, croppedAreaPixels, rotation);
      onApply(file);
    } catch (e) {
      console.error('Error cropping image:', e);
      onCancel();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="font-display text-lg font-bold">फोटो क्रप गर्नुहोस्</h3>
          <button type="button" onClick={onCancel} className="p-2 rounded-full hover:bg-paper-soft text-ink-soft hover:text-crimson transition-colors" aria-label="बन्द गर्नुहोस्">
            <X size={18} />
          </button>
        </div>

        <div className="relative w-full h-72 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <ZoomOut size={16} className="text-muted flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-crimson"
            />
            <ZoomIn size={16} className="text-muted flex-shrink-0" />
          </div>
          <div className="flex items-center gap-2">
            <RotateCw size={15} className="text-muted flex-shrink-0" />
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1 accent-crimson"
            />
            <span className="text-[11.5px] text-muted w-9 text-right">{rotation}°</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line">
          <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft transition-colors">
            रद्द
          </button>
          <button type="button" onClick={applyCrop} disabled={applying} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-crimson text-white text-[13px] font-semibold hover:bg-crimson-dark transition-colors disabled:opacity-60">
            <Check size={15} /> {applying ? 'क्रप हुँदै...' : 'क्रप गर्नुहोस्'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CircleCropModal;